import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { includes, max, omit } from 'lodash';
import { OrderPageIntent } from '../OrderPageIntent';
import OrderUtils, { SpecificEventError, VoucherError } from '../OrderUtils';
import { quotePrice } from '../core/pricing';
import {
  AccountData,
  AccountService,
  EventService,
  OrderPageData,
  OrderPageTypes,
  SlimEventData,
  VoucherOut,
  VoucherService,
  VoucherStatus,
  VoucherType,
} from '../../api';
import { useLanguage } from '../../utils/GeneralUtils';
import { OrderPageState } from './OrderPageState';
import { setUTMData } from '../orderCompletion/utmLocalStorage';
import { clearOrderPageStateFromLocalStorage, getOrderPageStateFromLocalStorage } from './orderPageStateLocalStorage';
import { getOrderPage, getOrderPages } from '../../utils/AccountUtils';
import { safeStartSessionRecording } from '../../utils/safePosthog';
import { sellsBothVoucherTypes, sellsMonetaryVouchers } from '../utils/stepHelpers';

type initOrderPageStateProps = {
  account: AccountData;
  voucher?: VoucherOut | null;
  voucherError?: VoucherError;
  specificEvent?: SlimEventData | null;
  specificEventError?: SpecificEventError;
};

type useInitOrderPageStateProps = {
  orderPageState: OrderPageState;
  setOrderPageState: (orderPageState: OrderPageState) => void;
  orderPageIdOverride?: string;
  startsMonetaryGiftCard?: boolean;
};

const calculateInitialPersons = (
  orderPage: OrderPageData,
  sliderMinValue: number,
  voucher?: VoucherOut | null,
  preFilledPersons: string | undefined = undefined,
  onlyCouples = false,
): number => {
  if (onlyCouples) {
    return 2;
  }
  if (voucher?.persons) {
    return voucher.persons;
  }

  const hasMultipleTickets = orderPage.objects.some((obj: any) => obj.type === 'multiple_tickets_price');
  if (hasMultipleTickets) {
    return 0;
  }
  if (preFilledPersons) {
    const persons = parseInt(preFilledPersons, 10);
    if (persons) {
      return persons;
    }
  }
  return max([orderPage.persons, sliderMinValue]) || 0;
};

export const useInitOrderPageState = ({
  orderPageState,
  setOrderPageState,
  orderPageIdOverride,
  startsMonetaryGiftCard,
}: useInitOrderPageStateProps) => {
  const { accountId, orderPageId: orderPageIdParam } = useParams();
  const orderPageId = orderPageIdOverride ?? orderPageIdParam;
  const language = useLanguage();
  const { i18n } = useTranslation();
  const posthog = usePostHog();
  const searchParams = new URLSearchParams(window.location.search);
  const onlyVouchers = !!startsMonetaryGiftCard || searchParams.get('vouchers') === 'true';
  const onlyCouples = searchParams.get('couples') === 'true';
  const troubleshoot = searchParams.get('troubleshoot') === 'true';
  const isWalkIn = searchParams.get('walkin') === '1';
  const preSelectedDate = searchParams.get('date') || undefined;
  const preFilledPersons = searchParams.get('persons') || undefined;
  const pTime = searchParams.get('time');
  const preSelectedTime = pTime ? parseInt(pTime, 10) : undefined;
  const voucherCode = searchParams.get('vouchercode');
  const requestsMonetaryGiftCard =
    !!startsMonetaryGiftCard || (onlyVouchers && searchParams.get('type') === VoucherType.MONETARY);
  const specificEventId = searchParams.get('event');
  const initOrderPageState = ({
    account,
    voucher,
    voucherError,
    specificEvent,
    specificEventError,
  }: initOrderPageStateProps) => {
    const accountDefaultLanguage = account.default_system_language;
    if (accountDefaultLanguage !== language && !searchParams.get('language')) {
      void i18n.changeLanguage(accountDefaultLanguage);
    }
    const experience = account.experiences[0];

    let orderPage = getOrderPages(account)[0];
    if (orderPageId) {
      orderPage = getOrderPage(account, orderPageId);
    }

    // Lets customers buy a course for a group: shows the "how many people" slider on course checkouts
    // (1–10). We currently dom't allow slider customization so it's not comming from the backend with any settings.
    if (orderPage.order_page_type === OrderPageTypes.COURSE) {
      const sliderIdx = orderPage.objects.findIndex((o: any) => o.type === 'slider');
      if (sliderIdx === -1) {
        orderPage = {
          ...orderPage,
          objects: [
            {
              id: 'auto_course_persons',
              type: 'slider',
              data_type: 'persons',
              min_value: 1,
              max_value: 10,
              label: { [language]: i18n.t('order_page.persons') },
            } as any,
            ...orderPage.objects,
          ],
        };
      } else if ((orderPage.objects[sliderIdx] as any).max_value <= 1) {
        const newObjects = [...orderPage.objects];
        newObjects[sliderIdx] = { ...newObjects[sliderIdx], max_value: 10 } as any;
        orderPage = { ...orderPage, objects: newObjects };
      }
    }

    orderPageState.orderPageIntent = OrderPageIntent.BUY_NOW;

    const experienceVoucher = voucher?.type === VoucherType.MONETARY ? null : voucher;
    const monetaryVoucherCode = voucher?.type === VoucherType.MONETARY ? voucher.code : undefined;

    if (experienceVoucher) {
      orderPageState.orderPageIntent = OrderPageIntent.REDEEM_GIFT_CARD;
      orderPageState.isLoadingDates = true;
    } else if (requestsMonetaryGiftCard && !voucher && sellsMonetaryVouchers(orderPage)) {
      orderPageState.orderPageIntent = OrderPageIntent.MONETARY_GIFT_CARD;
      orderPageState.activeStep = startsMonetaryGiftCard ? 0 : sellsBothVoucherTypes(orderPage) ? 2 : 1;
    }

    // @ts-ignore
    const sliderMinValue = orderPage.objects.find((x: any) => x.type === 'slider')?.min_value || 0;

    const tempDetails = {
      ...orderPageState.details,
      persons: calculateInitialPersons(orderPage, sliderMinValue, experienceVoucher, preFilledPersons, onlyCouples),
      duration: experienceVoucher?.duration || orderPage.duration,
      custom_details: experienceVoucher?.custom_details || OrderUtils.getDefaultCustomDetails(orderPage),
      chosen_resources: experienceVoucher?.chosen_resources || orderPageState.details.chosen_resources,
      is_voucher_redemption: !!experienceVoucher,
      resource_option_id: orderPage.resource_options?.[0]?.id,
    };

    posthog.setPersonPropertiesForFlags({ accountId: account.id });
    posthog.setPersonProperties({
      accountId: account.id,
      accountName: account.name,
      orderPageId: orderPage.id,
      environment: import.meta.env.VITE_REACT_APP_ENVIRONMENT || 'local',
      context: 'order-page',
    });
    if (troubleshoot) {
      posthog.capture('Troubleshoot started', { orderPageId: orderPage.id });
    }
    const specificEventDateAndTimeDetails = specificEvent
      ? {
          dateAndTimeDetails: {
            date: specificEvent?.date,
            time: specificEvent?.time,
            courseId: specificEvent?.course_id,
          },
        }
      : {};
    const specificPreselectedDateAndTimeDetails = preSelectedDate
      ? {
          dateAndTimeDetails: {
            date: preSelectedDate,
            time: preSelectedTime,
          },
        }
      : {};
    const localStorageState = getOrderPageStateFromLocalStorage(orderPage.id);
    if (localStorageState?.details) {
      const restoredResourceOptionId = localStorageState.details.resource_option_id;
      const isRestoredOptionValid = (orderPage.resource_options || []).some(
        (resourceOption: { id: string }) => resourceOption.id === restoredResourceOptionId,
      );
      localStorageState.details.resource_option_id = isRestoredOptionValid
        ? restoredResourceOptionId
        : orderPage.resource_options?.[0]?.id;
    }

    const standaloneGiftCardModeChanged =
      !!localStorageState && !!localStorageState.specificOrderPage?.standaloneGiftCard !== !!startsMonetaryGiftCard;
    const queryParamsChanged =
      standaloneGiftCardModeChanged ||
      (localStorageState?.savedQueryString !== undefined &&
        localStorageState.savedQueryString !== window.location.search);
    if (queryParamsChanged) {
      clearOrderPageStateFromLocalStorage();
    }

    if (
      localStorageState &&
      !queryParamsChanged &&
      ((!orderPageState.voucher && !voucher) || (!!orderPageState.voucher && !!voucher))
    ) {
      setOrderPageState({
        ...orderPageState,
        ...omit(localStorageState, ['account', 'availableDates', 'isLoadingDates', 'orderPage', 'voucher']),
        orderPage,
        account,
        specificEvent,
        ...specificEventDateAndTimeDetails,
        ...specificPreselectedDateAndTimeDetails,
        voucher,
        prefilledVoucherCode: monetaryVoucherCode,
        isWalkIn,
        specificOrderPage: {
          onlyCouples,
          onlyVouchers,
          standaloneGiftCard: !!startsMonetaryGiftCard,
          troubleshoot,
          preSelectedDate,
        },
      });
    } else {
      setOrderPageState({
        ...orderPageState,
        specificEvent: specificEvent || undefined,
        account,
        experience,
        orderPage,
        ...specificEventDateAndTimeDetails,
        ...specificPreselectedDateAndTimeDetails,
        details: {
          ...tempDetails,
          price: experienceVoucher
            ? experienceVoucher.price
            : orderPageState.orderPageIntent === OrderPageIntent.MONETARY_GIFT_CARD
            ? 0
            : quotePrice({
                orderPage,
                details: tempDetails,
                dateDetails: specificEvent
                  ? specificEventDateAndTimeDetails.dateAndTimeDetails
                  : preSelectedDate
                  ? specificPreselectedDateAndTimeDetails?.dateAndTimeDetails
                  : orderPageState.dateAndTimeDetails,
                account,
              }).total,
        },
        isInitialized: true,
        voucher: voucher || undefined,
        prefilledVoucherCode: monetaryVoucherCode,
        voucherError,
        specificEventError,
        isWalkIn,
        specificOrderPage: {
          onlyCouples,
          onlyVouchers,
          standaloneGiftCard: !!startsMonetaryGiftCard,
          troubleshoot,
          preSelectedDate,
        },
      });
    }
  };
  useEffect(() => {
    void (async () => {
      const account = await AccountService.getAccount(accountId);

      let voucher;
      let voucherError;
      let specificEventError;
      let specificEvent = null;
      if (account?.marketing_integrations?.gtm_id) {
        TagManager.initialize({ gtmId: account.marketing_integrations.gtm_id });
      }
      if (voucherCode) {
        safeStartSessionRecording(posthog);
        posthog.capture('Voucher redemption started', { voucherCode });
        try {
          voucher = await VoucherService.getVoucher(voucherCode);
          if (!includes([VoucherStatus.ACTIVE], voucher.status)) {
            if (voucher.status === VoucherStatus.USED) {
              voucherError = VoucherError.ALREADY_USED;
            }
            if (voucher.status === VoucherStatus.ARCHIVED) {
              voucherError = VoucherError.ARCHIVED;
            }
            voucher = null;
          }
        } catch (e: any) {
          if (e.status === 404) {
            voucherError = VoucherError.NOT_FOUND;
          }
          if (e.status === 400 && e.body.detail === 'voucher is not valid') {
            voucherError = VoucherError.EXPIRED;
          }
        }
      }
      if (specificEventId) {
        specificEvent = await EventService.getSlimEvent(specificEventId);
        if (specificEvent.tickets_left < 1) {
          specificEventError = SpecificEventError.NO_MORE_TICKETS;
        }
      }
      initOrderPageState({ account, voucher, voucherError, specificEvent, specificEventError });
    })();
    setUTMData();
  }, []);
};
